'use client';

import { AlertTriangle, ArrowLeft, Download, Loader2, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { OrderStatusBadge } from '@/components/orders/order-status-badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorDisplay } from '@/components/ui/error-display';
import { useToast } from '@/components/ui/use-toast';
import { useGetLatestSyncError, useMarkOrderAsPaid, useOrderDetails, useRefundOrder, useRetryPayment, useSyncOrderToProvider } from '@/lib/api/orders';
import { formatMoney } from '@/lib/money';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';

import { OrderCustomerCard } from './components/order-customer-card';
import { OrderDetailsSkeleton } from './components/order-details-skeleton';
import { OrderItemsCard } from './components/order-items-card';
import { OrderPaymentsCard } from './components/order-payments-card';
import { OrderRefundHistoryCard } from './components/order-refund-history-card';
import { OrderRelatedCard } from './components/order-related-card';
import { OrderSubscriptionsCard } from './components/order-subscriptions-card';
import { OrderSummaryCard } from './components/order-summary-card';
import { OrderTimelineCard } from './components/order-timeline-card';
import { RetryPaymentDialog } from './components/retry-payment-dialog';
import { SyncErrorDetailsDialog } from './components/sync-error-details-dialog';
import { SyncOrderConfirmationDialog } from './components/sync-order-confirmation-dialog';
import { BackButton } from '@/components/ui/BackButton';
import { OrderDiscountCard } from './components/order-discount-card';
import { OrderNoteAttributesCard } from './components/order-note-attributes-card';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useFormatDate } from '@/hooks/use-format-date';
import { DateDisplay } from '@/components/date-display';
import { trpc } from '@/lib/api/trpc';
import { Order } from '@tagadapay/crm-entities';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

const SYNC_CONFIRMATION_EMAIL_THRESHOLD_DAYS = 7;
const SYNC_CONFIRMATION_EMAIL_THRESHOLD_MS = SYNC_CONFIRMATION_EMAIL_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;

export default withRequiredPermission(OrderDetailsPage, PERMISSIONS.ORDERS_VIEW);

function OrderDetailsPage() {
  const { t } = useTranslation('orders');
  const { id } = useParams();
  const { toast } = useToast();
  const { data: order, isLoading, error } = useOrderDetails(id!);
  const { orgRoutes } = useOrgNavigate();
  const { format: formatTz, isReady: isTzReady } = useFormatDate();
  const refundMutation = useRefundOrder();
  const syncMutation = useSyncOrderToProvider();
  const markAsPaidMutation = useMarkOrderAsPaid();

  const retryMutation = useRetryPayment();
  const utils = trpc.useUtils();
  const [retryDialogOpen, setRetryDialogOpen] = React.useState(false);
  const [retryDialogStep, setRetryDialogStep] = React.useState<'select' | 'result'>('select');
  const [customerInstruments, setCustomerInstruments] = React.useState<any[]>([]);
  const [retryPaymentResult, setRetryPaymentResult] = React.useState<any>(null);
  // The instrument of the last retry, so "Force payment" charges the same card.
  const [retryInstrumentId, setRetryInstrumentId] = React.useState<string | null>(null);

  const [isRefundDialogOpen, setIsRefundDialogOpen] = React.useState(false);
  const [refundRequestId, setRefundRequestId] = React.useState<string | null>(null);
  const [syncErrorDialogOpen, setSyncErrorDialogOpen] = React.useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = React.useState(false);

  const shouldCheckSyncError = !!(order && !(order as any).externalOrderId);
  const { data: syncErrorData } = useGetLatestSyncError(id!, shouldCheckSyncError);
  const syncError = syncErrorData?.syncError;

  // Function to check if order can be refunded
  const canRefund = React.useCallback(() => {
    if (!order) return false;
    const totalAmount = order.summaries?.[0]?.totalAdjustedAmount || 0;
    const refundedAmount = order.refundedAmount || 0;
    return totalAmount > refundedAmount && order.status === 'paid';
  }, [order]);

  // Handle refund action
  const handleRefund = React.useCallback(() => {
    if (!canRefund()) {
      toast({
        title: t('cannotRefundTitle'),
        description: t('cannotRefundDescription'),
        variant: 'destructive',
      });
      return;
    }
    setRefundRequestId(crypto.randomUUID());
    setIsRefundDialogOpen(true);
  }, [canRefund, toast, t]);

  // Handle refund confirmation
  const handleRefundConfirm = React.useCallback(() => {
    if (!id || refundRequestId === null) return;

    refundMutation.mutate(
      {
        orderId: id,
        requestId: refundRequestId,
        refundType: 'full',
      },
      {
        onSuccess: () => {
          setIsRefundDialogOpen(false);
        },
      },
    );
  }, [id, refundMutation, refundRequestId]);

  const handleMarkAsPaid = React.useCallback(() => {
    if (!id) return;
    markAsPaidMutation.mutate({ orderId: id });
  }, [id, markAsPaidMutation]);

  // Handle sync to provider — on Shopify stores older than 7 days, prompt the merchant
  // whether Shopify should also fire its customer confirmation email. WooCommerce has no
  // REST-level knob to suppress that email, so the popup is Shopify-only for now.
  const handleSyncToProvider = React.useCallback(() => {
    if (!id || !order) return;
    const storeType = (order.store as { type?: string | null } | undefined)?.type;
    const paidAtMs = order.paidAt ? new Date(order.paidAt).getTime() : 0;
    const isShopify = storeType === 'shopify';
    const isOlderThanThreshold =
      paidAtMs > 0 && Date.now() - paidAtMs > SYNC_CONFIRMATION_EMAIL_THRESHOLD_MS;

    if (isShopify && isOlderThanThreshold) {
      setSyncDialogOpen(true);
      return;
    }
    syncMutation.mutate({ orderId: id, byPassConfirmationEmail: false });
  }, [id, order, syncMutation]);

  const handleSyncDialogConfirm = React.useCallback(
    (byPassConfirmationEmail: boolean) => {
      if (!id) return;
      syncMutation.mutate(
        { orderId: id, byPassConfirmationEmail },
        { onSuccess: () => setSyncDialogOpen(false) },
      );
    },
    [id, syncMutation],
  );

  // Handle export
  const handleExport = React.useCallback(() => {
    if (!order) return;

    // Create CSV content
    const csvData = [
      ['Order ID', 'Status', 'Total', 'Currency', 'Customer', 'Created At'],
      [
        order.id,
        order.status,
        order.summaries?.[0]?.totalAdjustedAmount || 0,
        order.currency || 'USD',
        `${order.customer.firstName} ${order.customer.lastName}`,
        formatTz(order.createdAt, 'yyyy-MM-dd HH:mm:ss'),
      ],
    ];

    const csvContent = csvData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `order-${order.id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: t('common:success', { ns: 'common' }),
      description: t('orderExportedSuccess'),
    });
  }, [order, toast, t, formatTz]);

  const handleRetryPayment = React.useCallback(async () => {
    if (!id) return;
    try {
      const data = await utils.orders.getCustomerPaymentInstruments.fetch({ orderId: id });
      const instruments = data.instruments;

      if (!instruments || instruments.length === 0) {
        toast({
          title: t('error'),
          description: t('noPaymentMethods'),
          variant: 'destructive',
        });
        return;
      }

      if (instruments.length === 1) {
        setRetryInstrumentId(instruments[0].id);
        retryMutation.mutate(
          { orderId: id, paymentInstrumentId: instruments[0].id },
          {
            onSuccess: (result) => {
              setRetryPaymentResult(result.payment);
              setCustomerInstruments(instruments);
              setRetryDialogStep('result');
              setRetryDialogOpen(true);
            },
          },
        );
        return;
      }

      setCustomerInstruments(instruments);
      setRetryDialogStep('select');
      setRetryDialogOpen(true);
    } catch {
      toast({
        title: t('error'),
        description: t('retryPaymentError'),
        variant: 'destructive',
      });
    }
  }, [id, utils, retryMutation, toast, t]);

  const handleConfirmRetry = React.useCallback(
    (paymentInstrumentId: string) => {
      if (!id) return;
      setRetryInstrumentId(paymentInstrumentId);
      retryMutation.mutate(
        { orderId: id, paymentInstrumentId },
        {
          onSuccess: (result) => {
            setRetryPaymentResult(result.payment);
            setRetryDialogStep('result');
          },
        },
      );
    },
    [id, retryMutation],
  );

  // After a `no_stored_credential` refusal, the person confirmed: charge the same card anyway.
  const handleForceRetry = React.useCallback(() => {
    if (!id || !retryInstrumentId) return;
    retryMutation.mutate(
      { orderId: id, paymentInstrumentId: retryInstrumentId, force: true },
      {
        onSuccess: (result) => {
          setRetryPaymentResult(result.payment);
          setRetryDialogStep('result');
        },
      },
    );
  }, [id, retryInstrumentId, retryMutation]);

  const handleCloseRetryDialog = React.useCallback(() => {
    setRetryDialogOpen(false);
    setRetryDialogStep('select');
    setRetryPaymentResult(null);
  }, []);

  if (isLoading) {
    return <OrderDetailsSkeleton />;
  }

  if (error) {
    return (
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <ErrorDisplay error={parseTRPCError(error)} />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold">{t('orderNotFound')}</h2>
        </div>
      </div>
    );
  }

  const hasRelatedOrders = (order as any).parentOrderId || (order as any).refundOrderIds?.length > 0;
  const showSyncButton = order.status === 'paid' && !(order as any).externalOrderId;
  const showMarkAsPaidButton = order.status === 'to_be_paid';
  const showRetryPaymentButton = Order.OrderBusiness.canRetryPayment(order);
  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <BackButton to={orgRoutes.ORDERS()} />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">{t('orderDetails')}</h1>
                <OrderStatusBadge status={order.status} />
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                <DateDisplay value={order.createdAt} />
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            {order.orderNumber && (
              <CopyableResourceBadge name={t('table.columns.orderNumber')} value={`#${order.orderNumber}`} copyValue={String(order.orderNumber)} variant="outline" />
            )}
            <CopyableResourceBadge name="Order ID" value={order.id} variant="outline" />
            <div className="flex gap-2">
              {showSyncButton && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSyncToProvider}
                  disabled={syncMutation.isPending}
                >
                  {syncMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  {t('sync')}
                </Button>
              )}
              {showRetryPaymentButton && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryPayment}
                  disabled={retryMutation.isPending}
                >
                  {retryMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  {t('retryPayment')}
                </Button>
              )}
              {showMarkAsPaidButton && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkAsPaid}
                  disabled={markAsPaidMutation.isPending}
                >
                  {markAsPaidMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('markAsPaid')}
                </Button>
              )}
              {canRefund() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefund}
                  disabled={refundMutation.isPending}
                >
                  {refundMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  {t('refund')}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleExport} disabled={!isTzReady}>
                <Download className="mr-2 h-4 w-4" />
                {t('export')}
              </Button>
            </div>
          </div>
        </div>

        {/* Test Order Warning */}
        {order.draft && (
          <Alert variant="warning" className="mt-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('testOrderTitle')}</AlertTitle>
            <AlertDescription>{t('testOrderDescription')}</AlertDescription>
          </Alert>
        )}

        {/* Sync Failure Warning */}
        {shouldCheckSyncError && syncError && (
          <Alert
            variant="warning"
            className="mt-6 cursor-pointer"
            onClick={() => setSyncErrorDialogOpen(true)}
          >
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('syncFailureTitle')}</AlertTitle>
            <AlertDescription>{t('syncFailureDescription')}</AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Main content - 2/3 width */}
          <div className="space-y-6 lg:col-span-2">
            <OrderSummaryCard order={order} />
            <OrderItemsCard order={order} />
            <OrderPaymentsCard order={order} />
            <OrderDiscountCard order={order} />
            <OrderRefundHistoryCard order={order} />
            <OrderSubscriptionsCard order={order} />
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-6 lg:sticky lg:top-0 lg:h-fit">
            <OrderCustomerCard order={order} />
            <OrderNoteAttributesCard order={order} />
            <OrderTimelineCard order={order} />
            {hasRelatedOrders && <OrderRelatedCard order={order} />}
          </div>
        </div>
      </div>

      {/* Refund Dialog */}
      <Dialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('refundOrder')}</DialogTitle>
            <DialogDescription>{t('confirmRefundMessage')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('orderTotal')}</span>
                <span className="font-medium">
                  {formatMoney(order.summaries?.[0]?.totalAdjustedAmount || 0, order.currency || 'USD')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('alreadyRefunded')}</span>
                <span className="font-medium">
                  {formatMoney(order.refundedAmount || 0, order.currency || 'USD')}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2 text-sm font-semibold">
                <span>{t('refundAmount')}</span>
                <span>
                  {formatMoney(
                    (order.summaries?.[0]?.totalAdjustedAmount || 0) - (order.refundedAmount || 0),
                    order.currency || 'USD',
                  )}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRefundDialogOpen(false)}>
              {t('common:cancel', { ns: 'common' })}
            </Button>
            <Button variant="destructive" onClick={handleRefundConfirm} disabled={refundMutation.isPending}>
              {refundMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('confirmRefund')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Error Details Dialog */}
      <SyncErrorDetailsDialog
        open={syncErrorDialogOpen}
        onOpenChange={setSyncErrorDialogOpen}
        syncError={syncError}
      />

      {/* Retry Payment Dialog */}
      <RetryPaymentDialog
        isOpen={retryDialogOpen}
        onClose={handleCloseRetryDialog}
        instruments={customerInstruments}
        payment={retryPaymentResult}
        isLoading={retryMutation.isPending}
        onSelectInstrument={handleConfirmRetry}
        onForce={handleForceRetry}
        step={retryDialogStep}
      />

      {/* Sync Confirmation Email Dialog (Shopify orders older than 7 days) */}
      <SyncOrderConfirmationDialog
        open={syncDialogOpen}
        onOpenChange={setSyncDialogOpen}
        onConfirm={handleSyncDialogConfirm}
        isPending={syncMutation.isPending}
      />
    </div>
  );
}
